// NPM Imports
import React, { Fragment, Suspense, PureComponent } from "react";
import { connect } from "react-redux";

// Component Imports
import ComponentDragBtn from "./DocumentItemActionBtns/ComponentDragBtn";
import { LazyLoad } from "../../commonComponents";
import DocumentItemActionBtns from "./DocumentItemActionBtns";
import PageNumber from "../PageNumber/PageNumber";

// Action Imports
import {
  updateDocumentClickedComponentID,
  updateHoveredDocumentSectionId,
  updateDraggedSection,
  preventComponentToDeactivate,
  forceUpdateEditorSectionIdAction,
  coverpageThemeAction,
  updateComponentMeta,
} from "../../actions/DocumentActions/index";
import { QuotationActions } from "../../actions";
import {
  addClause,
  removeClause,
  addClauseOptions,
  editClauseOption,
} from "../../actions/DocumentTemplateActions/templateActions";
import ToolbarActions from "../../actions/ToolbarActions";
import EditorActions from "../../actions/EditorActions";

// Methods Imports
import { isEditableTextAreaCOEditor } from "./methods";

// Internal Imports
import { DOCUMENTS } from "../../constants";
import { DocumentUtils } from "../../utils";

// Lazy loading imports
const CustomTextLazy = LazyLoad(() =>
  import(/* webpackChunkName: "custom-text" */ "./CustomText"),
);
const CustomImageLazy = LazyLoad(() =>
  import(/* webpackChunkName: "custom-image" */ "./CustomImage"),
);
const CustomDealTableLazy = LazyLoad(() =>
  import(/* webpackChunkName: "custom-deal-table" */ "./CustomDealTable"),
);
const CustomQuoteTableLazy = LazyLoad(() =>
  import(/* webpackChunkName: "custom-quote-table" */ "./CustomQuoteTable"),
);
const CustomPageBreakLazy = LazyLoad(() =>
  import(/* webpackChunkName: "custom-pagebreak" */ "./CustomPageBreak"),
);
const CustomLineSpaceLazy = LazyLoad(() =>
  import(/* webpackChunkName: "custom-linespace" */ "./CustomLineSpace"),
);
const CustomQuoteTableWrapperLazy = LazyLoad(() =>
  import(
    /* webpackChunkName: "custom-quotetable-wrapper" */ "./CustomQuoteTableWrapper"
  ),
);
const EmptyDocumentPlaceholderLazy = LazyLoad(() =>
  import(
    /* webpackChunkName: "empty-placeholder" */ "./EmptyDocumentPlaceholder"
  ),
);
const GoogleSheetContainerLazy = LazyLoad(() =>
  import(/* webpackChunkName: "gsheet-container" */ "./GoogleSheetContainer"),
);
const StripePaymentContainerLazy = LazyLoad(() =>
  import(
    /* webpackChunkName: "stripe-payment-container" */ "./StripePaymentContainer"
  ),
);
const TableAdapterLazy = LazyLoad(() =>
  import(/* webpackChunkName: "table-adapter" */ "../RevvTableV2/TableAdapter"),
);
const SignatureContainerLazy = LazyLoad(() =>
  import(/* webpackChunkName: "signature-container" */ "./SignatureContainer"),
);

// Destructuring
const {
  toggleTabView,
  setSectionDataForActiveSignatureInRightToolPane,
  setSectionDataForActivePaymentButtonInRightToolPane,
  setSidePaneDataForPageNo,
} = QuotationActions;
const { DOCUMENT_MODE, DOCUMENT_COMPONENT_TYPES } = DOCUMENTS;
const { updateAddComponentinToolbarAction } = ToolbarActions;
const { getComponentFromInfo, emptyContainer } = DocumentUtils;
const { nukeActiveEditor } = EditorActions;

class DocumentItems extends PureComponent {
  sectionActionBtnsRef = React.createRef();

  state = {
    replaceImage: false,
    hiddenColumnsForGsheet:
      this.props.section &&
      this.props.section.data &&
      this.props.section.data.attributes &&
      (this.props.section.type === DOCUMENT_COMPONENT_TYPES.GOOGLE_SHEET ||
        this.props.section.type === DOCUMENT_COMPONENT_TYPES.TABLE ||
        this.props.section.data.attributes.showManageColumnOption) &&
      this.props.section.data.attributes.hiddenCols
        ? this.props.section.data.attributes.hiddenCols
        : [],
  };

  componentDidMount() {
    if (!this.props.isLocked) {
      if (
        this.props.section &&
        this.props.section.data &&
        this.props.section.data.attributes &&
        this.props.section.data.attributes.hiddenCols
      ) {
        this.setState({
          hiddenColumnsForGsheet: this.props.section.data.attributes.hiddenCols,
        });
      }
    }
  }

  _handleClone = () => {
    const { section, rowIndex, colIndex } = this.props;
    console.debug("---section---", section);
    this.props.cloneSection(section, rowIndex, colIndex, true);
  };

  _clearReplaceImage = () => {
    this.setState(prevState => ({
      replaceImage: !prevState.replaceImage,
    }));
    this.props.disablePlusBtnHandler(false);
    const { updateDocumentClickedComponentID: updateDocumentClicked, section } =
      this.props;

    updateDocumentClicked(section.id, false, section.type);
  };

  // Get the document component item class names based on component types
  _getItemViewClassName = () => {
    const { isSharedDocument, section, isComponentActive } = this.props;
    let className = "component-items "; // Default class name all section

    // additional class name for PageBreak section
    if (section.type === DOCUMENT_COMPONENT_TYPES.PAGE_BREAK) {
      className += " pagebreak-component-items ";
    }
    // additional class name for LineSpace section
    if (section.type === DOCUMENT_COMPONENT_TYPES.LINE_SPACE) {
      className += " linespace-component-items ";
    }
    // additional class name for Table section
    if (section.type === DOCUMENT_COMPONENT_TYPES.TABLE) {
      className += " table-component-items  ";
    }
    if (isComponentActive) {
      className += " active active-clicked isactivecomponent";
    }

    // If it is a "Shared Document"
    if (isSharedDocument) {
      className += " shareddocument-component-items ";
    }

    return className;
  };

  _updateGsheetHiddenColumn = hiddenCols => {
    this.setState({ hiddenColumnsForGsheet: hiddenCols });
  };

  _redirectToDealTable = () => {
    if (!this.props.isSharedDocument) {
      this.props.toggleTabView("DEAL_TABLE");
    }
  };

  _getSectionView = () => {
    const {
      mode,
      itemType,
      sectionID,
      section,
      component,
      componentMeta,
      isLocked,
      openDealTable,
      clauses,
      updateComponent,
      isSharedDocument,
      isDocumentLocked,
      isComponentActive,
      showDocumentDiffView,
      currentEditorState,
      removeHighlightSelection,
      updateHoveredSectionId,
    } = this.props;
    const { type } = section;
    const sectionData = {
      mode,
      sectionID,
      section,
      component,
      componentMeta,
      isLocked,
      clauses,
      isDocumentLocked,
      isComponentActive,
      handleBlur: updateComponent,
      updateHoveredSectionId,
    };
    switch (type) {
      case DOCUMENT_COMPONENT_TYPES.EMPTY_SIGNATURE_CONTAINER:
        return (
          <Suspense fallback="">
            <SignatureContainerLazy
              {...sectionData}
              createDocSectionRef={this._createDocSectionRef}
              signatureWrapperRef={this.props.signatureWrapperRef}
              totalRowItems={this.props.totalRowItems}
              pdfScale={this.props.pdfScale}
              signatureTagType={type}
            />
          </Suspense>
        );
      case DOCUMENT_COMPONENT_TYPES.SIGNATURE_DATE:
        return (
          <Suspense fallback="">
            <SignatureContainerLazy
              {...sectionData}
              createDocSectionRef={this._createDocSectionRef}
              signatureWrapperRef={this.props.signatureWrapperRef}
              totalRowItems={this.props.totalRowItems}
              pdfScale={this.props.pdfScale}
              signatureTagType={type}
            />
          </Suspense>
        );
      case DOCUMENT_COMPONENT_TYPES.SIGNATURE_TEXT:
        return (
          <Suspense fallback="">
            <SignatureContainerLazy
              {...sectionData}
              createDocSectionRef={this._createDocSectionRef}
              signatureWrapperRef={this.props.signatureWrapperRef}
              totalRowItems={this.props.totalRowItems}
              pdfScale={this.props.pdfScale}
              signatureTagType={type}
            />
          </Suspense>
        );
      case DOCUMENT_COMPONENT_TYPES.SIGNATURE_CHECKBOX:
        return (
          <Suspense fallback="">
            <SignatureContainerLazy
              {...sectionData}
              createDocSectionRef={this._createDocSectionRef}
              signatureWrapperRef={this.props.signatureWrapperRef}
              totalRowItems={this.props.totalRowItems}
              pdfScale={this.props.pdfScale}
              signatureTagType={type}
            />
          </Suspense>
        );
      case DOCUMENT_COMPONENT_TYPES.SIGNATURE_NAME:
        return (
          <Suspense fallback="">
            <SignatureContainerLazy
              {...sectionData}
              createDocSectionRef={this._createDocSectionRef}
              signatureWrapperRef={this.props.signatureWrapperRef}
              totalRowItems={this.props.totalRowItems}
              pdfScale={this.props.pdfScale}
              signatureTagType={type}
            />
          </Suspense>
        );
      case DOCUMENT_COMPONENT_TYPES.SIGNATURE_INITIALS:
        return (
          <Suspense fallback="">
            <SignatureContainerLazy
              {...sectionData}
              createDocSectionRef={this._createDocSectionRef}
              signatureWrapperRef={this.props.signatureWrapperRef}
              totalRowItems={this.props.totalRowItems}
              pdfScale={this.props.pdfScale}
              signatureTagType={type}
            />
          </Suspense>
        );
      case DOCUMENT_COMPONENT_TYPES.GOOGLE_SHEET:
        return (
          <Suspense fallback="">
            <GoogleSheetContainerLazy
              {...sectionData}
              hiddenCols={this.state.hiddenColumnsForGsheet}
              createDocSectionRef={this._createDocSectionRef}
              isSharedDocument={isSharedDocument}
            />
          </Suspense>
        );
      case DOCUMENT_COMPONENT_TYPES.HTML:
        return (
          <Suspense fallback="">
            <CustomTextLazy
              {...sectionData}
              rowIndex={this.props.rowIndex}
              addComponent={this.props.addComponent}
              editClauseOption={this.props.editClauseOption}
              removeHighlightSelection={removeHighlightSelection}
              showDocumentDiffView={showDocumentDiffView}
              currentEditorState={currentEditorState}
              clauseOptionsDetails={this.props.clauseOptionsDetails}
              setRefsByType={this.props.setRefsByType}
              createDocSectionRef={this._createDocSectionRef}
              updateDocumentClickedComponentID={
                this.props.updateDocumentClickedComponentID
              }
              customToolbarRef={this.props.customToolbarRef}
              isSharedDocument={this.props.isSharedDocument}
              match={this.props.match}
              isEditableTextAreaCOPT={this.props.isEditableTextAreaCOPT}
            />
          </Suspense>
        );
      case DOCUMENT_COMPONENT_TYPES.TABLE:
        return (
          <Suspense fallback="">
            <TableAdapterLazy
              mode={this.props.mode}
              rowIndex={this.props.rowIndex}
              addComponent={this.props.addComponent}
              sectionID={this.props.sectionID}
              section={this.props.section}
              component={this.props.component}
              componentMeta={this.props.componentMeta}
              isLocked={this.props.isLocked}
              clauses={this.props.clauses}
              isDocumentLocked={this.props.isDocumentLocked}
              handleBlur={this.props.updateComponent}
              updateHoveredSectionId={this.props.updateHoveredSectionId}
              removeHighlightSelection={removeHighlightSelection}
              showForm={this.props.showForm}
              showDocumentDiffView={showDocumentDiffView}
              readOnly={isLocked || section.lock}
              sectionActionBtnsRef={this.sectionActionBtnsRef}
              customToolbarRef={this.props.customToolbarRef}
              createDocSectionRef={this._createDocSectionRef}
              tableFor="DOCUMENT_SECTION"
              className="revv-table"
              totalRowItems={this.props.totalRowItems}
              disablePlusBtnHandler={this.props.disablePlusBtnHandler}
              tableColorpickerDropdownRef={
                this.props.tableColorpickerDropdownRef
              }
              openTableColorpicker={this.props.openTableColorpicker}
              closeTableColorpicker={this.props.closeTableColorpicker}
              tableDataSourceDropDownRef={this.props.tableDataSourceDropDownRef}
              tableMoreOptionsDropDownRef={
                this.props.tableMoreOptionsDropDownRef
              }
              tableQuoteViewDropDownRef={this.props.tableQuoteViewDropDownRef}
              tableSelectedRowRef={this.props.tableSelectedRowRef}
              tableSelectedColRef={this.props.tableSelectedColRef}
              tableNcolOptionsDropdownRef={
                this.props.tableNcolOptionsDropdownRef
              }
              isSharedDocument={this.props.isSharedDocument}
              tableOptionsDetails={this.props.clauseOptionsDetails}
            />
          </Suspense>
        );
      case DOCUMENT_COMPONENT_TYPES.IMAGE:
        return (
          <Suspense fallback="">
            <CustomImageLazy
              {...sectionData}
              replaceImage={this.state.replaceImage}
              openFileBrowser={this._clearReplaceImage}
              setRefsByType={this.props.setRefsByType}
              createDocSectionRef={this._createDocSectionRef}
              totalRowItems={this.props.totalRowItems}
              disablePlusBtnHandler={this.props.disablePlusBtnHandler}
              imageFormFieldDetails={this.props.clauseOptionsDetails}
              match={this.props.match}
              isSharedDocument={this.props.isSharedDocument}
              updateComponentMeta={this.props.updateComponentMeta}
            />
          </Suspense>
        );
      case DOCUMENT_COMPONENT_TYPES.DEAL_TABLE:
        return (
          <Suspense fallback="">
            <CustomDealTableLazy
              {...sectionData}
              openDealTable={openDealTable}
              showDocumentDiffView={showDocumentDiffView}
              isSharedDocument={isSharedDocument}
              createDocSectionRef={this._createDocSectionRef}
              redirectToDealTable={() => this._redirectToDealTable()}
            />
          </Suspense>
        );
      case DOCUMENT_COMPONENT_TYPES.QUOTE_TABLE:
        return (
          <Suspense fallback="">
            <CustomQuoteTableLazy
              {...sectionData}
              openQuoteTable={openDealTable}
              isSharedDocument={isSharedDocument}
              createDocSectionRef={this._createDocSectionRef}
              redirectToDealTable={() => this._redirectToDealTable()}
            />
          </Suspense>
        );
      case DOCUMENT_COMPONENT_TYPES.QUOTE_TABLE_WRAPPER:
        return (
          <Suspense fallback="">
            <CustomQuoteTableWrapperLazy
              {...sectionData}
              isSharedDocument={isSharedDocument}
              redirectToDealTable={() => this._redirectToDealTable()}
              createDocSectionRef={this._createDocSectionRef}
            />
          </Suspense>
        );
      case DOCUMENT_COMPONENT_TYPES.EMPTY_CONTAINER: {
        /**
         * Here, need to hide the empty doc placeholder GIF in the following cases,
         * - if it is a blank editable(and "DRAFT" stage) Document or Template,
         * - if there is any dragged section(add components case)
         */
        let hidePlaceholder = false;
        let docStage = null;
        if (this.props.mode === DOCUMENT_MODE.DOCUMENT) {
          if (!this.props.showForm) {
            docStage = this.props.stage;
          }
        } else if (this.props.mode === DOCUMENT_MODE.TEMPLATE) {
          docStage = this.props.templateStatus;
        }
        if (
          this.props.draggedSection ||
          isSharedDocument ||
          docStage !== "DRAFT"
        ) {
          hidePlaceholder = true;
        }
        return (
          <Suspense fallback="">
            <EmptyDocumentPlaceholderLazy
              itemType={itemType}
              hidePlaceholder={hidePlaceholder}
            />
          </Suspense>
        );
      }
      case DOCUMENT_COMPONENT_TYPES.PAGE_BREAK:
        return (
          <Suspense fallback="">
            <CustomPageBreakLazy
              {...sectionData}
              createDocSectionRef={this._createDocSectionRef}
            />
          </Suspense>
        );
      case DOCUMENT_COMPONENT_TYPES.LINE_SPACE:
        return (
          <Suspense fallback="">
            <CustomLineSpaceLazy
              {...sectionData}
              createDocSectionRef={this._createDocSectionRef}
            />
          </Suspense>
        );
      case DOCUMENT_COMPONENT_TYPES.STRIPE_PAYMENT_BUTTON:
        return (
          <Suspense fallback="">
            <StripePaymentContainerLazy
              createDocSectionRef={this._createDocSectionRef}
              isSharedDocument={this.props.isSharedDocument}
              {...sectionData}
            />
          </Suspense>
        );
      case DOCUMENT_COMPONENT_TYPES.PAGE_NUMBER:
        return <PageNumber {...sectionData} />;
      default:
        return null;
    }
  };

  _createDocSectionRef = element => {
    this.docSectionRef = element;
    this.props.setRefsByType("DOCUMENT_SECTION", this.docSectionRef);
  };

  _getImageDimension = data =>
    new Promise(res => {
      const img = new Image();
      img.onload = () => {
        const newDimensions = this._reduceDimensions(img.width, img.height);
        res(newDimensions);
      };
      img.src = data;
    });

  render() {
    const { section, sectionID } = this.props;
    if (!section || section.id !== sectionID) {
      return null;
    }

    const { isLocked, isSharedDocument, isComponentActive, draggedSection } =
      this.props;
    const { id, lock } = section;
    let className = "doc-component ";
    if (lock && !isLocked) {
      className += " locked-component ";
    }
    if (
      isLocked &&
      section.type !== "Image" &&
      !this.props.isEditableTextAreaCOPT
    ) {
      className += " edit-locked  ";
    }
    const allowedTypes = [
      "Image",
      "Signature",
      "SignatureName",
      "SignatureDate",
      "SignatureInitials",
      "SignText",
      "SignCheckbox",
      "Table",
    ]; // list for next validity
    if (allowedTypes.includes(section.type)) {
      if (isComponentActive) {
        className += " image-options ";
      }
      if (isLocked) {
        if (!(this.props.showForm && section.clauseOptions)) {
          className += " edit-locked ";
        }
      } else if (
        section.clauseOptions &&
        (this.props.mode === DOCUMENT_MODE.TEMPLATE ||
          (this.props.mode === DOCUMENT_MODE.TEMPLATE && this.props.showForm))
      ) {
        if (section.type === "Table") {
          className += " df-component-table ";
        } else {
          className += " locked-component ";
        }
      }
    }
    if (isSharedDocument) {
      className += " shared-document-component";
    }

    const isDragged = draggedSection && section.id === draggedSection.id;

    return (
      <Fragment>
        {/* -------- Document item view section -------- */}
        <div
          ref={this.docSectionRef}
          data-test="documentitems-component-item-inside-template-doc-wrapper"
          id={section.id}
          className={`${this._getItemViewClassName()} ${
            section.type === DOCUMENT_COMPONENT_TYPES.EMPTY_CONTAINER
              ? "empty-container"
              : ""
          } ${isDragged ? "dragged-component" : ""}`}>
          <div
            data-test="documentitems-component-item-inside-template-doc"
            className={className}
            id={`document-item-section-${id}`}
            key={id}>
            {/* ------ Document Component Drag button ------ */}
            <ComponentDragBtn
              draggedSection={this.props.draggedSection}
              section={this.props.section}
              isLocked={this.props.isLocked}
              itemType={this.props.itemType}
              rowIndex={this.props.rowIndex}
              colIndex={this.props.colIndex}
              isComponentActive={this.props.isComponentActive}
            />

            {/* ------ Document Component Action's buttons section ------ */}
            {(!draggedSection ||
              this.props.draggedFrom === "new-component") && (
              <DocumentItemActionBtns
                isLocked={this.props.isLocked}
                mode={this.props.mode}
                totalRowItems={this.props.totalRowItems}
                isSharedDocument={this.props.isSharedDocument}
                section={this.props.section}
                itemType={this.props.itemType}
                component={this.props.component}
                isComponentActive={this.props.isComponentActive}
                clauseOptionsDetails={this.props.clauseOptionsDetails}
                // refs props
                sectionActionBtnsRef={this.sectionActionBtnsRef}
                docDeletePopupRef={this.props.docDeletePopupRef}
                // method props
                cloneSection={this._handleClone}
                handleComponentLockUnlock={this.props.handleComponentLockUnlock}
                disablePlusBtnHandler={this.props.disablePlusBtnHandler}
                updateComponent={this.props.updateComponent}
                deleteComponent={this.props.deleteComponent}
                clearReplaceImage={this._clearReplaceImage}
                updateGsheetHiddenColumn={this._updateGsheetHiddenColumn}
              />
            )}

            {/* ------ Document Component View section ------ */}
            {this._getSectionView()}
          </div>
        </div>
      </Fragment>
    );
  }
}

const mapStateToProps = (state, props) => {
  const { mode, componentMeta } = props;
  let clauseList = [];
  let clauseOptionsDetails;
  let tempDNFields = [];
  let section = "";
  let templateStatus = null;
  const {
    clickedActiveComponentId,
    clickedActiveComponentType,
    components,
    document: { isPdfDocument, stage, showForm, isLocked, scope, docType },
    draggedFrom,
    activeComponentMeta,
    docRecipientList,
  } = state.DocumentStore;

  // get component using component meta data
  // this is to directly connect component to redux
  if (!isPdfDocument) {
    const component = getComponentFromInfo(components, componentMeta);

    section = component || props.component || emptyContainer();
  } else {
    section = props.component;
  }
  if (mode === DOCUMENT_MODE.DOCUMENT) {
    const {
      document: { fields_used: allDynamicFields },
      clauses,
    } = state.DocumentStore;
    clauseList = clauses;
    tempDNFields = allDynamicFields;
  } else if (mode === DOCUMENT_MODE.TEMPLATE) {
    const {
      template: { status },
      clauses,
      dynamicFields: { fields },
    } = state.TemplateStore;
    clauseList = clauses;
    tempDNFields = fields;
    templateStatus = status;
  }

  const { clauseID } = section;
  const clauses =
    clauseID && Array.isArray(clauseList)
      ? clauseList.find(
          clause =>
            (mode === DOCUMENT_MODE.TEMPLATE && clause.id === clauseID) ||
            (mode === DOCUMENT_MODE.DOCUMENT && clause.clause_id === clauseID),
        )
      : null;

  let tempClauseOptions = null;
  if (section && section.clauseOptions) {
    tempClauseOptions = section.clauseOptions;
  }

  if (tempClauseOptions && tempDNFields) {
    clauseOptionsDetails = tempDNFields.find(
      clause => clause.id === tempClauseOptions.id,
    );
  }

  // check if the section is in active state
  let isComponentActive = clickedActiveComponentId === section.id;
  if (isPdfDocument) {
    isComponentActive =
      isComponentActive && clickedActiveComponentType === section.type;
  }

  if (
    !isComponentActive &&
    activeComponentMeta &&
    section &&
    section.type === DOCUMENT_COMPONENT_TYPES.TABLE &&
    activeComponentMeta.componentType === section.type
  ) {
    const { componentId, tableCellId } = activeComponentMeta;
    isComponentActive =
      componentId === section.id && tableCellId === clickedActiveComponentId;
  }

  // add section meta data to section
  section.componentMeta = componentMeta;

  let isEditableTextAreaCOPT = false;
  if (
    mode === DOCUMENT_MODE.DOCUMENT &&
    showForm &&
    section.type === "HTML" &&
    clauseOptionsDetails
  ) {
    isEditableTextAreaCOPT = isEditableTextAreaCOEditor(
      mode,
      { scope, isLocked, stage, showForm, docType },
      docRecipientList,
      props.isSharedDocument,
      clauseOptionsDetails,
    );
  }

  if (isComponentActive) {
    return {
      draggedFrom,
      section,
      showForm,
      clauses,
      isComponentActive,
      clauseOptionsDetails,
      isPdfDocument,
      stage,
      templateStatus,
      isEditableTextAreaCOPT,
    };
  }

  return {
    draggedFrom,
    section,
    showForm,
    clauses,
    clauseOptionsDetails,
    isPdfDocument,
    stage,
    templateStatus,
    isEditableTextAreaCOPT,
  };
};

const mapDispatchToProps = {
  addClause,
  removeClause,
  addClauseOptions,
  editClauseOption,
  toggleTabView,
  updateAddComponentinToolbarAction,
  setSectionDataForActiveSignatureInRightToolPane,
  updateDocumentClickedComponentID,
  setSectionDataForActivePaymentButtonInRightToolPane,
  updateHoveredDocumentSectionId,
  updateDraggedSection,
  preventComponentToDeactivate,
  forceUpdateEditorSectionIdAction,
  coverpageThemeAction,
  setSidePaneDataForPageNo,
  updateComponentMeta,
  nukeActiveEditor,
};

export default connect(mapStateToProps, mapDispatchToProps)(DocumentItems);
